import {
    Box,
    Button,
    Flex,
    HStack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Badge,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Custom components
import Card from 'components/card/Card';
import EmptyState from 'components/states/EmptyState';
import ErrorState from 'components/states/ErrorState';
import Loader from 'components/Loader';
import { MdSportsScore } from 'react-icons/md';
import { translate } from '../../../i18n';
import { CalendarIllustration } from 'components/illustrations';
import { GameDto } from '../../../models/game/dto/game.dto';
import { tableHeaderTextProps, tableProps, tableRowProps, tableCellProps, tableThProps, tableThFlexProps } from '../../../theme/constants';
import { typography } from 'theme/tokens';
import { formatDate } from '../../game-details/GameDetailsPage';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetLatestBookedGames } from '../hooks/useGetLatestBookedGames';

const columnHelper = createColumnHelper<GameDto>();

const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - new Date(date).getTime()) / 1000);

  const formatTimeUnit = (value: number, singularKey: string, pluralKey: string) => {
    const unit = value === 1 ? translate(singularKey as any) : translate(pluralKey as any);
    const ago = translate('game.time_ago.ago' as any);

    // Check if we're in French mode (when ago is "il y a")
    if (ago === 'il y a') {
      return `${ago} ${value} ${unit}`;
    } else {
      return `${value} ${unit} ${ago}`;
    }
  };

  if (diffInSeconds < 60) {
    return formatTimeUnit(diffInSeconds, 'game.time_ago.second', 'game.time_ago.seconds');
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return formatTimeUnit(diffInMinutes, 'game.time_ago.minute', 'game.time_ago.minutes');
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return formatTimeUnit(diffInHours, 'game.time_ago.hour', 'game.time_ago.hours');
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return formatTimeUnit(diffInDays, 'game.time_ago.day', 'game.time_ago.days');
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return formatTimeUnit(diffInWeeks, 'game.time_ago.week', 'game.time_ago.weeks');
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  return formatTimeUnit(diffInMonths, 'game.time_ago.month', 'game.time_ago.months');
};

export default function LatestBookedGamesTable() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const limit = 10;
  const { selectedOrganization } = useSelectedOrganization();

  const { data, isLoading, isInitialLoading, isFetching, isError } = useGetLatestBookedGames({
    page: currentPage,
    limit,
    organizationId: selectedOrganization?.id,
  });
  // `enabled: !!organizationId`. The `!selectedOrganization` guard below failed
  // safe (complete re-derivation of the single gate), but a store-derived gate
  // is D62's fragile pattern; route the never-issued case through the query's
  // own state instead.
  const readState = classifyRead(isError, isInitialLoading, data);

  // Reset page when organization changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedOrganization?.id]);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const columns = useMemo(() => [
    columnHelper.accessor('id', {
      id: 'id',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('game.game_id')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          #{info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('creationTime', {
      id: 'creationTime',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('game.booked_at')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {formatRelativeTime(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('startTime', {
      id: 'startTime',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('game.start_time')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {formatDate(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('organiser', {
      id: 'organiser',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('game.organiser')}
        </Text>
      ),
      cell: (info) => {
        const organiser = info.getValue();
        const fullName = organiser?.firstName && organiser?.lastName
          ? `${organiser.firstName} ${organiser.lastName}`
          : organiser?.email || translate('common.not_available' as any);
        return (
          <Text color={textColor} fontSize="sm" fontWeight="500">
            {fullName}
          </Text>
        );
      },
    }),
    columnHelper.accessor('court.name', {
      id: 'court',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('game.court')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {info.getValue() || translate('common.not_available' as any)}
        </Text>
      ),
    }),
    columnHelper.accessor('participants', {
      id: 'participants',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('game.participants')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {info.getValue()?.length || 0} / {info.row.original.capacity}
        </Text>
      ),
    }),
    columnHelper.accessor('sport', {
      id: 'sport',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('game.sport')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text {...tableHeaderTextProps}>
          {translate('game.status')}
        </Text>
      ),
      cell: (info) => {
        const status = info.getValue();
        const translatedStatus = translate(`game.${status}` as any);
        const colorScheme = status === 'completed' ? 'green'
          : status === 'canceled' ? 'red'
          : status === 'upcoming' ? 'orange'
          : status === 'score_proposed' ? 'blue'
          : status === 'score_disputed' ? 'red'
          : status === 'admin_review' ? 'purple'
          : 'gray';
        return (
          <Badge
            colorScheme={colorScheme}
            borderRadius="full"
            px="10px"
            py="3px"
            fontSize="11px"
            fontWeight="600"
            textTransform="capitalize"
          >
            {translatedStatus}
          </Badge>
        );
      },
    }),
  ], [textColor]);

  const table = useReactTable({
    data: data?.games || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    initialState:{
        columnVisibility:{
            id:false,
        }
    }
  });

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (data?.pagination.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRowClick = (gameId: number) => {
    navigate(`/admin/gameDetails?game=${gameId}`);
  };

  // Prompt for the organization when the read was never issued (no org
  // selected). Previously a `!selectedOrganization` store guard; now driven by
  // the query's own state (D62).
  if (readState === 'never-issued') {
    return (
      <Card flexDirection="column" w="100%" px="0px">
        <ScopePrompt gate="organization" />
      </Card>
    );
  }

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
        <Box>
          <Text color={textColor} {...typography.pageTitle}>
            {translate('game.latest_booked_games')}
          </Text>
          <Text color="gray.500" fontSize="sm">
            {translate('game.click_row_to_view_details')}
          </Text>
        </Box>

        {/* Pagination controls in header */}
        {data?.pagination && (
          <HStack spacing={4}>
            <Text fontSize="sm" color="gray.600">
              {translate('game.page_of')} {currentPage + 1} {translate('game.of')} {data.pagination.totalPages}
              {data.pagination.totalCount > 0 && ` (${data.pagination.totalCount} ${translate('game.total')})`}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                onClick={handlePreviousPage}
                isDisabled={currentPage === 0 || isLoading || isFetching}
                variant="outline"
              >
                {translate('game.previous')}
              </Button>
              <Button
                size="sm"
                onClick={handleNextPage}
                isDisabled={!data.pagination.hasNextPage || isLoading || isFetching}
                variant="outline"
              >
                {translate('game.next')}
              </Button>
            </HStack>
          </HStack>
        )}
      </Flex>

      <Box>
        <Table {...tableProps}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    {...tableThProps}
                    borderColor={borderColor}
                  >
                    <Flex {...tableThFlexProps}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr
                key={row.id}
                {...tableRowProps}
                onClick={() => handleRowClick(row.original.id)}
              >
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    {...tableCellProps}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>

        {/* Loading indicator */}
        {/* `isFetching` alone — see SupportRequestsTable: `isLoading` is pinned
          * true on a disabled query, so the old disjunction was a
          * spinner-forever kept unreachable only by the `never-issued` early
          * return above (D62). Identical over every reachable state. */}
        {isFetching && <Loader isLoading={true} variant="spinner" />}

        {/* Error state */}
        {/* `unreadable`, not the raw `error` object: this table renders its rows
          * unconditionally, so a failed background refetch planted a full error
          * block under a list of good bookings rather than blanking it
          * (R96-3). */}
        {readState === 'unreadable' && (
          <ErrorState message={translate('game.error_loading_games')} />
        )}

        {/* Empty state */}
        {!isFetching && readState !== 'unreadable' && (!data?.games || data.games.length === 0) && (
          <EmptyState
            illustration={<CalendarIllustration />}
            title={translate('walkthrough.empty_games_title')}
            description={translate('walkthrough.empty_games_description')}
          />
        )}
      </Box>
    </Card>
  );
}